import React, { FC } from 'react';
import { useEditorContext } from '../../Editor/Context';
import SelectionBubbleMenu from '../SelectionBubbleMenu';
import cn from 'classnames';
import Bold from '../buttons/Bold';
import Italic from '../buttons/Italic';
import Underline from '../buttons/Underline';
import Strike from '../buttons/Strike';
import Link from '../buttons/Link';
import Separator from '../buttons/Separator';
import Alignment from '../buttons/Alignment';
import BlockFormatting from '../buttons/BlockFormatting';
import BulletList from '../buttons/BulletList';
import OrderedList from '../buttons/OrderedList';
import Blockquote from '../buttons/Blockquote';
import ClearFormatting from '../buttons/ClearFormatting';
import { getNodeFromSelection, isExtensionInstalled } from '../../utils/tiptap';
import HRule from '../buttons/HRule';
import {
  LinkBubbleMenuHandlerStorage,
  LinkMenuState,
} from '../../../extensions/extension-link-menu-handler/linkMenuHandler';
import Outdent from '../buttons/Outdent';
import Indent from '../buttons/Indent';

export interface Props {
  omitHRule?: boolean;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

const FormatBubbleMenu: FC<Props> = ({
  scrollContainerRef,
  omitHRule = false,
}) => {
  const editor = useEditorContext();

  if (!editor) {
    return null;
  }

  if (!editor?.isEditable) {
    return null;
  }

  let buttonCount = 14;

  if (!isExtensionInstalled(editor, 'textAlignment')) {
    buttonCount--;
  }

  if (!isExtensionInstalled(editor, 'heading')) {
    buttonCount--;
  }

  if (!isExtensionInstalled(editor, 'bulletList')) {
    buttonCount--;
  }

  if (!isExtensionInstalled(editor, 'orderedList')) {
    buttonCount--;
  }

  if (!isExtensionInstalled(editor, 'blockquote')) {
    buttonCount--;
  }

  if (omitHRule) {
    buttonCount--;
  }

  const handlerStorage = editor.storage
    .linkBubbleMenuHandler as LinkBubbleMenuHandlerStorage;
  const menuState = handlerStorage.state;

  let workaroundLinkBubble = false;
  if (menuState === LinkMenuState.VIEW_LINK_DETAILS) {
    workaroundLinkBubble = true;
  }

  let open = editor!.state.selection.empty === false;

  // Dont show for any of our exts
  const nodes = getNodeFromSelection(editor);
  if (
    nodes[0] &&
    nodes[0].type.name === 'blockquote' &&
    nodes[1] &&
    nodes[1].type.name.startsWith('da-')
  ) {
    open = false;
  }
  if (nodes[0] && nodes[0].type.name.startsWith('da-')) {
    open = false;
  }

  return (
    <SelectionBubbleMenu
      scrollContainerRef={scrollContainerRef}
      menuHeight={38}
      menuWidth={48 * buttonCount}
      editor={editor}
      open={open}
      forceBelowSelection={workaroundLinkBubble}
    >
      <div
        className={cn('ds-surface-tertiary')}
        style={{ maxWidth: '100vw', display: 'flex', flexWrap: 'wrap' }}
      >
        <BlockFormatting editor={editor} />
        <Bold editor={editor} />
        <Italic editor={editor} />
        <Underline editor={editor} />
        <Strike editor={editor} />

        <Separator editor={editor} />

        <Link editor={editor} />

        <Separator hideWhen={'link'} editor={editor} />

        <Alignment editor={editor} />
        <Indent editor={editor} />
        <Outdent editor={editor} />
        <Blockquote editor={editor} />
        <OrderedList editor={editor} />
        <BulletList editor={editor} />
        {!omitHRule && <HRule editor={editor} />}

        <Separator editor={editor} />

        <ClearFormatting editor={editor} />
      </div>
    </SelectionBubbleMenu>
  );
};
FormatBubbleMenu.displayName = 'FormatBubbleMenu';

export default FormatBubbleMenu;
